// src/components/resources/tools/generators/FakeChat/SlackPreview.tsx

'use client'

import { cn } from '~/lib/utils'
import { useFakeChatActions, useFakeChatState } from './FakeChatContext'
import type { ChatMessage, ChatParticipant } from './FakeChat.types'
import { useResolvedMessages } from './useResolvedMessages'
import { getTheme, type AccentTheme } from './accentThemes'

/**
 * Props for SlackMessageRow component
 * @interface SlackMessageRowProps
 * @property {ChatMessage} message - The message to display.
 * @property {ChatParticipant} [participant] - The participant who sent the message.
 * @property {boolean} showAvatars - Whether to show the avatar.
 * @property {boolean} showTimestamps - Whether to show the timestamp.
 * @property {boolean} showReactions - Whether to show the reactions.
 * @property {AccentTheme} theme - The theme to use for the message.
 */
interface SlackMessageRowProps {
  message: ChatMessage
  participant?: ChatParticipant
  showAvatars: boolean
  showTimestamps: boolean
  showReactions: boolean
  theme: AccentTheme
  isContinuation: boolean
}

/**
 * Individual message row component for Slack style
 */
function SlackMessageRow({
  message,
  participant,
  showAvatars,
  showTimestamps,
  showReactions,
  theme,
  isContinuation
}: SlackMessageRowProps): JSX.Element {
  const { toggleReaction } = useFakeChatActions()
  const shouldShowHeader = !isContinuation || !showAvatars
  const timestamp = showTimestamps ? message.timestamp : null

  // Dispatch a single toggle and let the reducer decide add vs remove from LIVE
  // state. This subtree renders from a deferred snapshot, so branching here on
  // `message.reactions` could mis-decide if the user taps faster than the
  // deferred render commits (see DeferredFakeChatStateProvider).
  const handleReactionClick = (emoji: string): void => {
    toggleReaction(message.id, emoji)
  }

  return (
    <div className="group relative flex gap-4 px-6 py-4 transition-colors hover:bg-[#f8f8f8]">
      {showAvatars ? (
        <div className="flex w-10 justify-center pt-1">
          <span
            className={cn(
              'flex h-9 w-9 items-center justify-center rounded-md text-sm font-semibold text-white transition-opacity duration-150',
              participant?.avatarColor ?? 'bg-[#505359]',
              isContinuation ? 'opacity-0' : 'opacity-100'
            )}
          >
            {participant?.avatarInitials ?? '?'}
          </span>
        </div>
      ) : null}
      <div className="flex-1 space-y-2">
        {shouldShowHeader ? (
          <div className="flex items-baseline gap-2">
            <p className="text-sm font-semibold text-[#1d1c1d]">{participant?.displayName ?? 'Unknown sender'}</p>
            {participant?.handle ? <span className="text-xs text-[#5e5d61]">{participant.handle}</span> : null}
            {timestamp ? (
              <time className="ml-auto text-xs text-[#616061] opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                {timestamp}
              </time>
            ) : null}
          </div>
        ) : timestamp ? (
          <div className="flex justify-start">
            <time className="text-xs text-[#616061] opacity-0 transition-opacity duration-200 group-hover:opacity-100">
              {timestamp}
            </time>
          </div>
        ) : null}
        <p className="text-[15px] leading-6 text-[#1d1c1d]">{message.text}</p>
        {message.attachments?.map(attachment => (
          <div
            key={attachment.id}
            className="mt-2 rounded-md border border-[#dadce0] bg-white p-3 text-sm text-[#1d1c1d] shadow-[0_1px_0_rgba(0,0,0,0.08)]"
          >
            <p className="font-medium">{attachment.title}</p>
            {attachment.description ? <p className="mt-1 text-xs text-[#616061]">{attachment.description}</p> : null}
          </div>
        ))}
        {showReactions ? (
          <div className="flex flex-wrap items-center gap-1 pt-1">
            {message.reactions.map(reaction => (
              <button
                key={reaction.id}
                type="button"
                onClick={() => handleReactionClick(reaction.emoji)}
                className={cn(
                  'inline-flex items-center gap-1 rounded-full border border-[#dadce0] bg-white px-2 py-1 text-xs text-[#1d1c1d] hover:bg-[#f4f4f5] transition-colors cursor-pointer',
                  reaction.reactedByViewer ? theme.slackReaction : ''
                )}
              >
                {reaction.emoji}
                <span>{reaction.count}</span>
              </button>
            ))}
            <button
              type="button"
              onClick={() => handleReactionClick('👍')}
              className="inline-flex items-center gap-1 rounded-full border border-transparent px-2 py-1 text-xs text-[#616061] opacity-0 transition-opacity duration-200 hover:bg-[#f4f4f5] group-hover:opacity-100"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M12 5v14" />
                <path d="M5 12h14" />
              </svg>
              Add reaction
            </button>
          </div>
        ) : null}
        {message.isThreaded ? (
          <button
            type="button"
            className={cn(
              'inline-flex items-center gap-2 text-xs transition-colors hover:text-[#0b4a75]',
              theme.slackThread || 'text-[#1264a3]'
            )}
          >
            <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#f4f4f5] text-current">↪</span>
            <span>{message.threadReplyCount ?? 1} replies</span>
          </button>
        ) : null}
      </div>
      <div className="absolute right-6 top-3 hidden items-center gap-1 text-[#616061] group-hover:flex">
        <button
          type="button"
          className="flex h-7 w-7 items-center justify-center rounded hover:bg-[#ebebed]"
          aria-label="React"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M7 10v12" />
            <path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2a3.13 3.13 0 0 1 3 3.88Z" />
          </svg>
        </button>
        <button
          type="button"
          className="flex h-7 w-7 items-center justify-center rounded hover:bg-[#ebebed]"
          aria-label="Reply in thread"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z" />
          </svg>
        </button>
        <button
          type="button"
          className="flex h-7 w-7 items-center justify-center rounded hover:bg-[#ebebed]"
          aria-label="More actions"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <circle cx="12" cy="12" r="1" />
            <circle cx="19" cy="12" r="1" />
            <circle cx="5" cy="12" r="1" />
          </svg>
        </button>
      </div>
    </div>
  )
}

function SlackComposer({ theme, channelName }: { theme: AccentTheme; channelName: string }): JSX.Element {
  // Slack always prefixes channels with #, so normalise user input before rendering.
  const normalizedChannel = channelName.startsWith('#') ? channelName : `#${channelName}`

  return (
    <footer className="flex-shrink-0 border-t border-[#e3e4e8] bg-white px-6 py-4">
      <div className="flex items-center rounded-md border border-[#c8c9cc] bg-white px-3 py-2 shadow-sm">
        <div className="flex items-center space-x-2 mr-2">
          <button
            type="button"
            className="flex h-6 w-6 items-center justify-center rounded text-[#616061] hover:bg-[#f4f4f5]"
            aria-label="Add attachment"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <circle cx="12" cy="12" r="10" />
              <path d="M12 8v8" />
              <path d="M8 12h8" />
            </svg>
          </button>
          <button
            type="button"
            className="flex h-6 w-6 items-center justify-center rounded text-[#616061] hover:bg-[#f4f4f5]"
            aria-label="Attach file"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48" />
            </svg>
          </button>
        </div>
        <input
          type="text"
          placeholder={`Message ${normalizedChannel}`}
          className="flex-1 bg-transparent text-sm text-[#1d1c1d] placeholder-[#616061] outline-none"
        />
        <div className="flex items-center space-x-2 ml-2">
          <button
            type="button"
            className="flex h-6 w-6 items-center justify-center rounded text-[#616061] hover:bg-[#f4f4f5]"
            aria-label="Mention someone"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <circle cx="12" cy="12" r="4" />
              <path d="M16 8v5a3 3 0 0 0 6 0v-1a10 10 0 1 0-4 8" />
            </svg>
          </button>
          <button
            type="button"
            className="flex h-6 w-6 items-center justify-center rounded text-[#616061] hover:bg-[#f4f4f5]"
            aria-label="Add emoji"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <circle cx="12" cy="12" r="10" />
              <path d="M8 14s1.5 2 4 2 4-2 4-2" />
              <line x1="9" x2="9.01" y1="9" y2="9" />
              <line x1="15" x2="15.01" y1="9" y2="9" />
            </svg>
          </button>
          <button
            type="button"
            className={cn(
              'flex h-9 w-9 items-center justify-center rounded text-white transition-colors',
              theme.slackComposer || 'bg-[#4A154B] hover:bg-[#5a1f5b]'
            )}
            aria-label="Send message"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="m22 2-7 20-4-9-9-4Z" />
              <path d="M22 2 11 13" />
            </svg>
          </button>
        </div>
      </div>
    </footer>
  )
}

/**
 * Slack platform preview component
 */
export function SlackPreview(): JSX.Element {
  const { conversation } = useFakeChatState()
  const { toggleSidebar } = useFakeChatActions()
  const theme = getTheme(conversation.metadata.accentColor)
  const resolvedMessages = useResolvedMessages(conversation.messages)
  // Sharing the resolved payload ensures grouping + participant lookups stay consistent with the other previews.
  const displayChannelName = conversation.metadata.channelName.startsWith('#')
    ? conversation.metadata.channelName
    : `#${conversation.metadata.channelName}`
  const participantCount = conversation.participants.length
  const workspaceInitial = conversation.metadata.workspaceName.charAt(0)?.toUpperCase() ?? 'A'
  const directMessages = conversation.participants.filter(participant => !participant.isSelf)
  const isSidebarCollapsed = conversation.metadata.sidebarCollapsed ?? false

  return (
    <div className="relative flex w-full overflow-hidden rounded-2xl border border-[#e3e4e8] bg-white text-[#1d1c1d] shadow-xl">
      <aside className={cn(
        'flex flex-col bg-[#4A154B] text-white transition-all duration-300',
        isSidebarCollapsed ? 'w-16' : 'w-50'
      )}>
        <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
          {!isSidebarCollapsed && (
            <span className="text-lg font-semibold truncate">{conversation.metadata.workspaceName}</span>
          )}
          <button
            type="button"
            onClick={toggleSidebar}
            className="flex h-8 w-8 items-center justify-center rounded bg-white/20 text-base font-semibold hover:bg-white/30 transition-colors"
            aria-label={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {workspaceInitial}
          </button>
        </div>
        <div className={cn(
          'flex-1 overflow-y-auto text-sm',
          isSidebarCollapsed ? 'px-2 py-4' : 'px-4 py-4'
        )}>
          <div className="space-y-2">
            <div className="flex items-center justify-between text-xs uppercase tracking-wide text-white/70">
              {!isSidebarCollapsed && <span>Channels</span>}
              {!isSidebarCollapsed && <span className="text-white/60">＋</span>}
            </div>
            <div className="space-y-1">
              <span className={cn(
                'flex items-center rounded py-1 text-white/70',
                isSidebarCollapsed ? 'justify-center px-1' : 'gap-2 px-2'
              )}>
                <span className="text-white/80">#</span>
                {!isSidebarCollapsed && <span>general</span>}
              </span>
              <span className={cn(
                'flex items-center rounded font-semibold text-[#4a154b] bg-white/95 shadow-sm',
                isSidebarCollapsed ? 'justify-center px-1 py-1' : 'gap-2 px-2 py-1'
              )}>
                <span>#</span>
                {!isSidebarCollapsed && <span>{conversation.metadata.channelName.replace(/^#?/, '')}</span>}
              </span>
              <span className={cn(
                'flex items-center rounded py-1 text-white/70',
                isSidebarCollapsed ? 'justify-center px-1' : 'gap-2 px-2'
              )}>
                <span className="text-white/80">#</span>
                {!isSidebarCollapsed && <span>design-sync</span>}
              </span>
            </div>
          </div>
          <div className="mt-6 space-y-2">
            <div className="flex items-center justify-between text-xs uppercase tracking-wide text-white/70">
              {!isSidebarCollapsed && <span>Direct messages</span>}
              {!isSidebarCollapsed && <span className="text-white/60">＋</span>}
            </div>
            <div className="space-y-1">
              {directMessages.map(dm => (
                <span key={dm.id} className={cn(
                  'flex items-center rounded py-1 text-white/80',
                  isSidebarCollapsed ? 'justify-center px-1' : 'gap-2 px-2'
                )}>
                  <span className="relative inline-flex h-2 w-2 items-center justify-center">
                    <span className="absolute h-2 w-2 rounded-full bg-emerald-400" aria-hidden />
                  </span>
                  {!isSidebarCollapsed && <span>{dm.displayName}</span>}
                </span>
              ))}
            </div>
          </div>
        </div>
        <div className={cn(
          'border-t border-white/10 text-xs text-white/70',
          isSidebarCollapsed ? 'px-2 py-3' : 'px-4 py-3'
        )}>
          <div className="flex items-center gap-2">
            <span className="inline-flex h-2 w-2 rounded-full bg-emerald-400" aria-hidden />
            {!isSidebarCollapsed && <span>{conversation.metadata.lastActivityLabel}</span>}
          </div>
        </div>
      </aside>
      <div className="flex flex-1 flex-col overflow-hidden">
        <header className="flex flex-wrap items-center justify-between gap-4 border-b border-[#e3e4e8] px-6 py-4 bg-white">
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-lg font-semibold">{displayChannelName}</span>
              <span className="inline-flex h-5 w-5 items-center justify-center rounded bg-[#f4f4f5] text-sm text-[#616061]">
                ★
              </span>
              <span className={cn('inline-flex items-center rounded-full px-3 py-1 text-xs font-medium', theme.slackBadge)}>
                {conversation.metadata.channelType === 'channel'
                  ? 'Public channel'
                  : conversation.metadata.channelType === 'dm'
                    ? 'Direct message'
                    : 'Private channel'}
              </span>
            </div>
            <p className="text-sm text-[#616061]">{conversation.metadata.channelTopic}</p>
          </div>
          <div className="flex items-center gap-3 text-sm text-[#616061]">
            <span className="inline-flex items-center gap-2 rounded-full border border-[#e3e4e8] bg-[#f8f8f8] px-3 py-1">
              <span className="inline-flex h-2 w-2 rounded-full bg-emerald-500" aria-hidden />
              {conversation.metadata.lastActivityLabel}
            </span>
            <span>{participantCount} members</span>
          </div>
        </header>
        <div className="flex-1 overflow-hidden">
          <div className="flex h-full flex-col overflow-y-auto px-6 py-6 bg-white">
            <div className="flex justify-center pb-4 text-xs text-[#616061]">
              <span className="rounded-full border border-[#e3e4e8] bg-white px-3 py-1">Today</span>
            </div>
            <div className="space-y-1">
              {resolvedMessages.map(({ message, participant, isContinuation }) => (
                <SlackMessageRow
                  key={message.id}
                  message={message}
                  participant={participant}
                  showAvatars={conversation.metadata.showAvatars}
                  showTimestamps={conversation.metadata.showTimestamps}
                  showReactions={conversation.metadata.showReactions}
                  theme={theme}
                  isContinuation={isContinuation}
                />
              ))}
            </div>
          </div>
        </div>
        {conversation.metadata.typingIndicator ? (
          <div className="border-t border-[#e3e4e8] px-6 py-4 text-xs text-[#616061]">
            <div className="inline-flex items-center gap-2 rounded-full border border-[#e3e4e8] bg-white px-3 py-2">
              <span className="text-[#1d1c1d]">Someone is typing</span>
              <span className="flex items-center gap-1">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#1264a3]" />
                <span
                  className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#1264a3]"
                  style={{ animationDelay: '0.12s' }}
                />
                <span
                  className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#1264a3]"
                  style={{ animationDelay: '0.24s' }}
                />
              </span>
            </div>
          </div>
        ) : null}
        <SlackComposer theme={theme} channelName={conversation.metadata.channelName} />
      </div>
    </div>
  )
}
